import { LoadingOutlined } from "@ant-design/icons";
import { Avatar, Image, Input, Modal, Spin, Upload } from "antd";
import { last } from "lodash";
import { useEffect, useMemo, useState } from "react";
import ImgCrop from "antd-img-crop";
import { AiFillEdit } from "react-icons/ai";
import { HiSave } from "react-icons/hi";
import { MdDelete } from "react-icons/md";
import apiFactory from "../../api";
import { useInfoUser } from "../../store/UserStore";
import {
  getColorFromInitial,
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import "./style.scss";
import { useImageLoader } from "../customHook/ImageLoader";
import { CiCamera } from "react-icons/ci";
import { GeneralModal } from "./GeneralModal";
import { compressImage, uploadFileV2 } from "../../utils/SendFile";

const ProfileModal = ({ isModalOpen, closeModal }) => {
  const {
    user,
    languageMap,
    updateUser,
    updateLanguageMap,
    updateStickerKeywords,
  } = useInfoUser();
  const [isEditMood, setIsEditMood] = useState(false);
  const [isEditPhone, setIsEditPhone] = useState(false);
  const [isEditPosition, setIsEditPosition] = useState(false);
  const [profile, setProfile] = useState(user);
  const [isLoading, setIsLoading] = useState(false);
  const [briefName, setBriefName] = useState();
  const [isPreview, setIsPreview] = useState(false);
  const [isRemoveAvatar, setIsRemoveAvatar] = useState(false);

  const { imageUrl, setImageUrl } = useImageLoader(user?.avatar);

  const onChangeProfile = (e, field) => {
    setProfile({
      ...profile,
      mood: field === "mood" ? e?.target?.value : profile?.mood,
      phone: field === "phone" ? e?.target?.value : profile?.phone,
      position: field === "position" ? e?.target?.value : profile?.position,
    });
  };

  const onStoreProfile = async (field) => {
    switch (field) {
      case "mood":
        setIsEditMood(false);
        break;
      case "phone":
        setIsEditPhone(false);
        break;
      case "position":
        setIsEditPosition(false);
        break;
      default:
        break;
    }

    const params = {
      language: user?.language,
      phone: profile?.phone,
      mood: profile?.mood,
      position: profile?.position,
    };

    const me = await apiFactory.userApi.saveMe(params);

    if (me?.status === 200) {
      updateUser(me?.data?.user);
      updateLanguageMap(me?.data?.languageMap);
      updateStickerKeywords(me?.data?.stickerKeywords);
    } else {
      openNotificationError(me?.message);
      updateUser(null);
    }
  };

  const removeAvatar = async () => {
    setIsLoading(true);
    const result = await apiFactory.userApi.remove();

    if (result?.status === 200) {
      updateUser({ ...user, avatar: result?.data });
      openNotificationSuccess(result?.message);
    } else {
      openNotificationError(result?.message);
    }

    setIsLoading(false);
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      openNotificationError(
        languageMap?.["error.onlyUpImg"] ?? "You can only upload image!",
      );
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleChange = async ({ file }) => {
    try {
      if (file.status === "uploading") return;

      setIsLoading(true);

      const MAX_AVATAR_SIZE = 500 * 1024;
      const MAX_DIMENSION = 500;

      let uploadFile = file?.originFileObj;

      if (
        uploadFile?.size > MAX_AVATAR_SIZE ||
        uploadFile.width > MAX_DIMENSION ||
        uploadFile.height > MAX_DIMENSION
      ) {
        uploadFile = await compressImage(file?.originFileObj);
      }

      const uploadRes = await uploadFileV2(uploadFile, null, "AVATAR");

      const res = await apiFactory.userApi.upload({
        avatar: uploadRes?.resourceId,
      });

      if (res?.status === 200) {
        openNotificationSuccess(
          languageMap?.["success.avtUpload"] ?? "Avatar uploaded successfully.",
        );
        setImageUrl(res.data);
        user.avatar = res.data;
        updateUser({ ...user });
      }
    } catch (e) {
      openNotificationError("Upload failed!");
    } finally {
      setIsLoading(false);
    }
  };

  const genderAvatar = useMemo(() => {
    return (
      <>
        <Avatar
          style={
            !imageUrl && {
              backgroundColor: getColorFromInitial(briefName),
              color: "white",
            }
          }
          size={80}
          onClick={() => setIsPreview(true)}
          src={imageUrl || undefined}
        >
          {!imageUrl && briefName}
        </Avatar>
        {isPreview && imageUrl && (
          <Image
            wrapperStyle={{
              display: "none",
            }}
            preview={{
              visible: isPreview,
              onVisibleChange: (visible) => setIsPreview(visible),
            }}
            src={imageUrl}
          />
        )}
      </>
    );
  }, [imageUrl, briefName, isPreview]);

  useEffect(() => {
    setBriefName(
      last(user?.name?.trim()?.split(" "))?.[0] ||
        last(user?.username?.trim()?.split(" "))?.[0],
    );
  }, [user]);

  return (
    <Modal
      title={languageMap?.["menu.profile.myProfile"] ?? "My Profile"}
      open={isModalOpen}
      onCancel={closeModal}
      footer={[]}
    >
      <div className="relative flex flex-col gap-[10px]">
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.avatar"] ?? "Avatar"}:
          </div>
          <div>
            <div className="relative inline-block w-20 h-20 cursor-pointer">
              {genderAvatar}
              <ImgCrop rotationSlider>
                <Upload
                  showUploadList={false}
                  beforeUpload={beforeUpload}
                  onChange={handleChange}
                  customRequest={({ file, onSuccess }) => {
                    onSuccess("ok");
                  }}
                >
                  <button
                    type="button"
                    className="hover:!bg-[#d9d9d9] absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md cursor-pointer"
                  >
                    <CiCamera className="text-xl" />
                  </button>
                </Upload>
              </ImgCrop>
            </div>
          </div>
          {user?.avatar && (
            <button onClick={() => setIsRemoveAvatar(true)}>
              <MdDelete size={20} color="#ef4444" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.id"] ?? "ID"}:
          </div>
          <div>{user?.userId} </div>
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.name"] ?? "Name"}:
          </div>
          <div>{user?.name} </div>
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.email"] ?? "Email"}:
          </div>
          <div>{user?.email}</div>
        </div>

        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.position"] ?? "Position"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.position}
            disabled={!isEditPosition}
            onChange={(e) => onChangeProfile(e, "position")}
            maxLength={50}
          />
          {isEditPosition ? (
            <button onClick={() => onStoreProfile("position")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditPosition(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.tel"] ?? "Tel"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.phone}
            disabled={!isEditPhone}
            maxLength={17}
            onChange={(e) => onChangeProfile(e, "phone")}
            onKeyPress={(event) => {
              if (!/[0-9.+-]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
          {isEditPhone ? (
            <button onClick={() => onStoreProfile("phone")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditPhone(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-[20px]">
          <div className="w-[100px] font-semibold">
            {languageMap?.["menu.profile.status"] ?? "Status"}:
          </div>
          <Input
            className="w-[300px]"
            value={profile?.mood}
            disabled={!isEditMood}
            onChange={(e) => onChangeProfile(e, "mood")}
          />
          {isEditMood ? (
            <button onClick={() => onStoreProfile("mood")}>
              <HiSave size={20} color="#2a56b9" />
            </button>
          ) : (
            <button onClick={() => setIsEditMood(true)}>
              <AiFillEdit size={20} color="#333333" />
            </button>
          )}
        </div>
        {isLoading && (
          <div className="absolute top-[30%] w-full flex justify-center">
            <Spin
              indicator={<LoadingOutlined className="text-[35px]" spin />}
            />
          </div>
        )}
      </div>
      {isRemoveAvatar && (
        <GeneralModal
          title={"Remove Avatar"}
          content={"Do you want to delete avatar?"}
          onCancel={() => setIsRemoveAvatar(false)}
          open={() => setIsRemoveAvatar(true)}
          onConfirm={removeAvatar}
        />
      )}
    </Modal>
  );
};

export { ProfileModal };
